import { combineReducers } from '@reduxjs/toolkit';
import {
  put,
  fork,
  select,
  take,
  call,
  cancel,
  takeEvery,
  takeLatest,
  delay,
} from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { Map, List, fromJS } from 'immutable';
import * as Project from './project';
import * as User from './user';
import * as PageItems from './pageItems';
import {
  ADD_PAGE_ITEMS,
  REMOVE_PAGE_ITEMS,
  updatePageItemsNoUndo,
} from './pageItems';
import * as Layouts from './layouts';
import * as Pages from './pages';
import * as Selectors from '../reducers/selectors';
import * as Sections from './sections';
import * as Images from './images';
import * as Selection from './selection';
import {
  SET_BACKGROUND_IMAGE,
  SET_BACKGROUND_COLOR,
  SET_BACKGROUND_COLOR_UNDOABLE,
  SET_BACKGROUND_COLOR_ALL_PAGES,
  SET_BACKGROUND_ALL_PAGES,
  SET_BACKGROUND_PATTERN,
} from './backgrounds';
import {
  IMAGE_ADDED,
  IMAGE_REMOVED,
  PAGE_UPDATED,
  PAGES_UPDATED,
  UPDATE_IMAGE_COUNTS,
} from '../sagas/ReturnedChangesetProcessor';
import { createThumbnail } from '../utils/thumbnailService';
import PicabooConstants from '../common/PicabooConstants';
import {
  showSuccessModal,
  showGenericErrorModal,
  showLoginModal,
  showModal,
} from './modals';
import {
  closeAutoFillPromptModal,
  keepSelectedTab,
  setSidePanelOpened,
} from './navigation';
import SpineHelper from '../helpers/SpineHelper';
import { hideModal, showProcessing } from '../ducks/modals';
import * as Navigation from './navigation';
import { isFirstPageOfPillow } from '../utils/pillowUtils';
import { isMobile } from '../components/PicabooResponsive';
import { PageRenderer } from '../containers/EditorContainer/WorkspaceContainer/CanvasContainer/PageRenderer';
import { setApplyLayoutCallback, setInsertAfterSpreadID } from './selection';
import { LOAD_PROJECT, LOAD_PROJECT_UNDOABLE, updateWarnings } from './project';
import { GraphicItemUtils } from '../utils/GraphicItemUtils';
import { logCustomEvent } from '../utils/TagManager';

import { stopThumbs } from './pages';
import { app2Service, spellCheckProject } from '../utils/zoomServices';

/** ***********************************************************************
 *  Constants
 *********************************************************************** */
export const ADD_CHANGESET = 'ADD_CHANGESET';
const NEW_CHANGE_ADDED = 'NEW_CHANGE_ADDED';
const CLEAR_PENDING_CHANGESETS = 'CLEAR_PENDING_CHANGESETS';

const CHANGESET_UPDATE_PAGES = 'changeset/UPDATE_PAGES';
const CHANGESET_UPDATE_PAGE_ITEMS = 'changeset/UPDATE_PAGE_ITEMS';
const CHANGESET_UPDATE_SPREAD = 'changeset/UPDATE_SPREAD';
const CHANGESET_SET_SPREAD_LIST = 'changeset/CHANGESET_SET_SPREAD_LIST';
const CHANGESET_APPLY_LAYOUT_TO_PAGES = 'changeset/APPLY_LAYOUT_TO_PAGES';
const CHANGESET_APPLY_PAGELAYOUT_TO_PAGES =
  'changeset/APPLY_PAGELAYOUT_TO_PAGES';
const CHANGESET_APPLY_PAGEDESIGN_TO_PAGE = 'changeset/APPLY_PAGEDESIGN_TO_PAGE';
const CHANGESET_APPLY_THEME_PAGES = 'changeset/APPLY_THEME_PAGES';
const CHANGESET_APPLY_FILTER_TO_ALL_PAGES =
  'changeset/CHANGESET_APPLY_FILTER_TO_ALL_PAGES';
const CHANGESET_APPLY_DROP_SHADOW_TO_ALL_PAGES =
  'changeset/CHANGESET_APPLY_DROP_SHADOW_TO_ALL_PAGES';
const CHANGESET_CONVERT_SPREAD_PAGES =
  'changeset/CHANGESET_CONVERT_SPREAD_PAGES';
const CHANGESET_ADD_IMAGE = 'changeset/CHANGESET_ADD_IMAGE';
const CHANGESET_REMOVE_IMAGE = 'changeset/CHANGESET_REMOVE_IMAGE';
const CHANGESET_ADD_PAGE_ITEMS = 'changeset/CHANGESET_ADD_PAGE_ITEMS';
const CHANGESET_REMOVE_PAGE_ITEMS = 'changeset/CHANGESET_REMOVE_PAGE_ITEMS';
const CHANGESET_ADD_IMAGE_REF = 'changeset/CHANGESET_ADD_IMAGE_REF';
const CHANGESET_DELETE_IMAGE_REF = 'changeset/CHANGESET_DELETE_IMAGE_REF';
const CHANGESET_ADD_IMAGE_TAG = 'changeset/CHANGESET_ADD_IMAGE_TAG';
const CHANGESET_TOGGLE_ORIENTATION = 'changeset/TOGGLE_ORIENTATION';
const CHANGESET_RENAME_PROJECT = 'changeset/RENAME_PROJECT';
const CHANGESET_UPDATE_HEADERS = 'changeset/UPDATE_HEADERS';
const CHANGESET_CHANGE_PROJECT_STYLE = 'changeset/CHANGE_PROJECT_STYLE';
const CHANGESET_DELETE_SPREAD = 'changeset/CHANGESET_DELETE_SPREAD';
const CHANGESET_DUPLICATE_SPREAD = 'changeset/CHANGESET_DUPLICATE_SPREAD';
const CHANGESET_ADD_SPREAD = 'changeset/CHANGESET_ADD_SPREAD';
const CHANGESET_AUTOFILL_PAGES = 'changeset/AUTOFILL_PAGES';
const CHANGESET_ROTATE_PRINT = 'changeset/ROTATE_PRINT';
const CHANGESET_UPDATE_PAGE_THUMB = 'changeset/CHANGESET_UPDATE_PAGE_THUMB';
const CHANGESET_UPDATE_ACCOUNT_PREFERENCE =
  'changeset/CHANGESET_UPDATE_ACCOUNT_PREFERENCE';
const CHANGESET_SET_BACKGROUND_COLOR_ALL_PAGES =
  'changeset/CHANGESET_SET_BACKGROUND_COLOR_ALL_PAGES';
const CHANGESET_SET_BACKGROUND_ALL_PAGES =
  'changeset/CHANGESET_SET_BACKGROUND_ALL_PAGES';

const CHANGESET_SAVING = 'changeset/SAVING';
const THUMB_DELAY = 'changeset/TIME_DELAY';

const CHANGESET_MUTATE_PAGES_IN_SECTION =
  'changeset/CHANGESET_MUTATE_PAGES_IN_SECTION';

const CHANGESET_ADD_PRINT_PAGE = 'changeset/CHANGESET_ADD_PRINT_PAGE';

/** ***********************************************************************
 *  Selectors
 *  *********************************************************************** */
const pendingChangeSetsSelector = (state) => state.changesets.pending;
const changeRequestIDSelector = (state) =>
  state.changesets.settings.changeRequestID;
// const changeSetsSettings = state => state.changesets.settings;
const thumbnailTasks = {};
export const savingSelector = (state) =>
  state.changesets.settings.saving || state.changesets.settings.thumbDelay;

/** ***********************************************************************
 *  reducers
 *  *********************************************************************** */
function pending(state = [], action) {
  switch (action.type) {
    case ADD_CHANGESET:
      const changeset = { ...action };
      delete changeset.type;
      return state.concat(changeset);

    case CLEAR_PENDING_CHANGESETS:
      return [];
  }

  return state;
}

function processing(state = [], action) {
  switch (action.type) {
  }

  return state;
}

const settingsDefaultState = {
  saving: false,
  lastSavedAt: null,
  changeRequestID: uuidv4().toUpperCase(), // unique for this instance
  thumbDelay: false,
};

function settings(state = settingsDefaultState, action) {
  switch (action.type) {
    case CHANGESET_SAVING:
      return {
        ...state,
        saving: action.isSaving,
        lastSavedAt: action.isSaving
          ? new Date().toLocaleTimeString()
          : state.lastSavedAt,
      };
    case THUMB_DELAY:
      return { ...state, thumbDelay: action.thumbDelay };
    case LOAD_PROJECT:
    case LOAD_PROJECT_UNDOABLE:
      return { ...state, thumbDelay: false };
  }

  return state;
}

const changesetReducer = combineReducers({
  pending,
  processing,
  settings,
});

/** ***********************************************************************
 *  actions
 *  *********************************************************************** */
export function thumbDelay(delay) {
  return {
    type: THUMB_DELAY,
    thumbDelay: delay,
  };
}

export function noChanges(action) {
  action.APPLYING_CHANGES = true;
  return action;
}

export function updatePage(page) {
  return updatePages([page]);
}

export function updatePages(pages) {
  return {
    type: CHANGESET_UPDATE_PAGES,
    pages,
  };
}

function pageUpdated(page) {
  return pagesUpdated([page]);
}

function pagesUpdated(pages) {
  return addChangeset(CHANGESET_UPDATE_PAGES, pages);
}

function updatePageItems(pageItems) {
  if (!Array.isArray(pageItems)) pageItems = Array.of(pageItems);

  return addChangeset(CHANGESET_UPDATE_PAGE_ITEMS, pageItems);
}

export function applyFilterToAll(pageID, pageItemID, filter) {
  return addChangeset(CHANGESET_APPLY_FILTER_TO_ALL_PAGES, {
    pageID,
    pageItemID,
    filter,
  });
}
export function applyDropShadowToAll(pageID, pageItemID) {
  return addChangeset(CHANGESET_APPLY_DROP_SHADOW_TO_ALL_PAGES, {
    pageID,
    pageItemID,
  });
}

export function updateSpread(spreadID, pages) {
  return {
    type: CHANGESET_UPDATE_SPREAD,
    spreadID,
    pages,
  };
}

export function setSpreadList(sections) {
  return {
    type: CHANGESET_SET_SPREAD_LIST,
    sections,
  };
}

export function updateAccountPreference(preferenceName, value) {
  return addChangeset(CHANGESET_UPDATE_ACCOUNT_PREFERENCE, {
    preferenceName,
    value,
  });
}

function addImage(image) {
  return addImages([image]);
}

function addPageItems(pageItems) {
  if (!Array.isArray(pageItems)) pageItems = Array.of(pageItems);

  return addChangeset(CHANGESET_ADD_PAGE_ITEMS, pageItems);
}

function removePageItems(items) {
  if (!Array.isArray(items)) items = Array.of(items);

  return addChangeset(CHANGESET_REMOVE_PAGE_ITEMS, items);
}

function addImages(images) {
  return addChangeset(CHANGESET_ADD_IMAGE, images);
}

export function removeImages(images) {
  return addChangeset(CHANGESET_REMOVE_IMAGE, images);
}

export function deleteSpread(spreadID) {
  return addChangeset(CHANGESET_DELETE_SPREAD, spreadID);
}

export function duplicateSpread(spreadID) {
  return addChangeset(CHANGESET_DUPLICATE_SPREAD, spreadID);
}

export function addSpread(sectionID, afterSpreadID, beforeSpreadID) {
  return addChangeset(CHANGESET_ADD_SPREAD, {
    sectionID,
    after: afterSpreadID,
    before: beforeSpreadID,
  });
}

function applyLayoutToPages(changes) {
  return addChangeset(CHANGESET_APPLY_LAYOUT_TO_PAGES, changes);
}

function applyPageLayoutToPages(changes) {
  return addChangeset(CHANGESET_APPLY_PAGELAYOUT_TO_PAGES, changes);
}

function applyPageDesignToPages(changes) {
  return addChangeset(CHANGESET_APPLY_PAGEDESIGN_TO_PAGE, changes);
}

function applyThemePages(changes) {
  return addChangeset(CHANGESET_APPLY_THEME_PAGES, changes);
}

export function autoFillPages(
  sortOrder = '',
  addPages,
  replaceAllWells,
  layoutType = 'standard',
  addText = false,
  insertAfterSpreadID = '',
) {
  return {
    type: CHANGESET_AUTOFILL_PAGES,
    sortOrder,
    addPages,
    replaceAllWells,
    layoutType,
    addText,
    insertAfterSpreadID,
  };
}

export function rotatePrint(pageID) {
  return {
    type: CHANGESET_ROTATE_PRINT,
    pageID,
  };
}

function convertSpreadPages(changes) {
  return addChangeset(CHANGESET_CONVERT_SPREAD_PAGES, changes);
}

function addChangeset(action, changeset) {
  return {
    type: ADD_CHANGESET,
    action,
    changeset,
  };
}

export function setSaving(isSaving) {
  return {
    type: CHANGESET_SAVING,
    isSaving,
  };
}

function changesetAdded() {
  return { type: NEW_CHANGE_ADDED };
}

/** ***********************************************************************
 *  Sagas
 *  *********************************************************************** */

function* pushChangesSaga() {
  //noinspection InfiniteLoopJS
  while (true) {
    const project = yield select(Selectors.projectMetadataSelector);

    if (project.get('isLoaded') == false) {
      yield delay(500);
      continue;
    }

    let pendingChanges = yield select(pendingChangeSetsSelector);

    if (pendingChanges.length == 0) {
      yield take(NEW_CHANGE_ADDED);
      // delay for just a bit in case we get more changesets right away.. i.e. 1 change has multiple changesets
      // like adding a sticker to a page and update page
      yield delay(50);
      pendingChanges = yield select(pendingChangeSetsSelector);
    }

    yield put(setSaving(true));
    yield put({ type: CLEAR_PENDING_CHANGESETS });

    try {
      const projectPK = yield select(Selectors.projectPKSelector);
      const lastModified = project.get('lastModified');
      const spreadIndex = yield select(Selectors.spreadIndexSelector);
      const spreads = yield select(Selectors.activeSpreadSelector);
      const spreadID = spreads.get(spreadIndex);
      let activePages = yield select(Selectors.selectedPages);
      activePages = activePages.toJS();
      let pageLastModified = lastModified; // 17517 editing spinetext fails when this is null
      if (activePages && activePages.length > 0) {
        pageLastModified = activePages[0].dateLastModified;
      }
      // const changesetSettings = yield select(changeSetsSettings);
      const changeRequestID = yield select(changeRequestIDSelector);
      const { data, error } = yield app2Service().post(
        '/ZoomProjectChanges/update',
        {
          projectPK,
          lastModified,
          changeRequestID: changeRequestID,
          pendingChanges,
          pageLastModified,
        },
      );

      yield put(setSaving(false));

      if (data && data.resultCode === 0 && data.result && data.result.changes) {
        for (let change of data.result.changes) {
          let msg = null;
          switch (change.type) {
            case 'PAGE_THUMB_UPDATED':
              msg = {
                type: Pages.PAGE_THUMB_UPDATED,
                pageID: change.changes.pageID,
                thumbURL: change.changes.thumbnailURL,
              };

              break;
            case 'PAGE_ITEM_UPDATED': {
              let updateSpellcheck = false;
              let updateLowRes = false;
              for (let item of change.changes) {
                if (item.itemType == 'richText' || item.itemType == 'caption') {
                  updateSpellcheck = true;
                  break;
                } else if (item.itemType == 'graphicItem') {
                  updateLowRes = true;
                  break;
                }
              }
              if (updateSpellcheck) {
                const pageItems = change.changes.map((item) => fromJS(item)); // needs to be array of immutable maps
                msg = PageItems.updatePageItemsSpellcheck(pageItems);
              } else if (updateLowRes) {
                const pageItems = change.changes.map((item) => fromJS(item)); // needs to be array of immutable maps
                msg = PageItems.updatePageItemsLowres(pageItems);
              }
              break;
            }
            case 'PAGE_LAST_UPDATE_CHANGED': {
              msg = Pages.updatePageLastUpdate(change.changes);
              break;
            }
            case 'IMAGE_ADDED': {
              msg = {
                type: IMAGE_ADDED,
                image: change.changes,
              };

              if (msg.image.serverImage == 1) {
                let pageItems = yield select(Selectors.pageItemsSelector);
                const itemOnPage = pageItems.some(function (pageItem) {
                  return msg.image.imageID === pageItem.get('imageID');
                });
                if (itemOnPage) {
                  yield put(msg);
                  msg = Navigation.renderCanvasFromStore();
                }
              } else {
                //For single page products, if this is the first image then populate the well
                const isSinglePageProduct = yield select(
                  Selectors.isSinglePageProduct,
                );
                if (isSinglePageProduct) {
                  const images = yield select(Selectors.projectImagesSelector);
                  if (images.size === 0) {
                    const pageItems = yield select(Selectors.pageItemsSelector);

                    let emptyWellPI = null;
                    pageItems.forEach((pi) => {
                      if (pi.get('itemType') === 'graphicItem') {
                        if (!pi.get('imageID')) {
                          emptyWellPI = pi;
                        }
                      }
                    });

                    if (emptyWellPI) {
                      const pages = yield select(Selectors.pagesSelector);
                      const page = pages.first();

                      const ratio = page.get('ratio');
                      const ratioBase = page.get('ratioBase');

                      const dim = PageRenderer.calcPageDimensions(
                        {
                          clientWidth: 1000,
                          clientHeight: 1000,
                        },
                        ratio,
                        -1,
                        ratioBase,
                      );

                      const obj = emptyWellPI.toJS();
                      const wellWidth = obj.width * dim.ratioWidth;
                      const wellHeight = obj.height * dim.ratioWidth;

                      const fit = GraphicItemUtils.fitImage(
                        msg.image.width,
                        msg.image.height,
                        wellWidth,
                        wellHeight,
                      );
                      const imgWidth = fit.width / dim.ratioWidth;
                      const imgHeight = fit.height / dim.ratioWidth;

                      let newPI = emptyWellPI.withMutations((list) => {
                        list
                          .set('imageID', msg.image.imageID)
                          .set('image_width', imgWidth)
                          .set('image_height', imgHeight)
                          .set(
                            'image_positionX',
                            ((fit.width - wellWidth) / 2 / dim.ratioWidth) * -1,
                          )
                          .set(
                            'image_positionY',
                            ((fit.height - wellHeight) / 2 / dim.ratioWidth) *
                              -1,
                          );
                      });

                      yield put(msg);
                      yield put(PageItems.updatePageItems([newPI]));
                      msg = Navigation.renderCanvasFromStore(page.get('id'));
                    }
                  }
                }
              }

              break;
            }
            case 'LAYOUT_APPLIED':
              yield put(hideModal());
              yield put({
                type: PAGES_UPDATED,
                pages: change.changes.pages,
                leftovers: change.changes.leftovers,
                replace: false,
              });
              yield put(
                keepSelectedTab(Selection.keepSelection(Pages.reloadSpread())),
              );
              break;
            case 'DESIGN_APPLIED':
              yield put(
                keepSelectedTab(
                  Selection.keepSelection(Pages.reloadSpreadUndoable()),
                ),
              );
              break;
            case 'AUTOFILL_APPLIED':
              let successTextKey = 'AUTOFILLMODAL.Success_Text';
              const { originalPageCount, updatedPageCount } = change.changes;

              if (originalPageCount !== updatedPageCount) {
                const productInfo = yield select(
                  Selectors.projectProductInfoSelector,
                );
                if (
                  SpineHelper.spineWillChange(
                    originalPageCount,
                    updatedPageCount,
                    productInfo.get('spineSizes'),
                  )
                ) {
                  successTextKey = 'AUTOFILLMODAL.Success_Text_SpineChange';
                }
              }

              yield put(closeAutoFillPromptModal());
              yield put(
                showSuccessModal('AUTOFILLMODAL.Success_Title', successTextKey),
              );
              // this is an easter egg
              msg = Project.loadProjectUndoable(projectPK);
              logCustomEvent('auto_create_success_modal');
              break;
            case 'PROJECT_STYLE_CHANGED':
              let action = Project.loadProject(projectPK, null, 'first', true);
              action.uploadThumbAfterLoad = true;
              yield put(keepSelectedTab(action));
              const productId = yield select(
                Selectors.projectProductIDSelector,
              );
              //reload layouts if product is changing
              if (productId !== change.changes.previewSize) {
                yield put(
                  Layouts.getCurrentLayouts(change.changes.previewSize),
                );
              }
              break;
            case 'ORIENTATION_TOGGLED':
              yield put(Layouts.setCurrentLayouts(change.changes.layouts));
              msg = Project.loadProjectUndoable(projectPK);
              break;
            case 'SPREAD_CONVERTED':
              msg = keepSelectedTab(Pages.reloadSpreadUndoable());
              break;
            case 'NAME_CHANGED':
              msg = {
                type: Project.NAME_CHANGED,
                name: change.changes['name'],
                author: change.changes['author'],
              };

              break;
            case 'HEADERS_CHANGED':
              msg = {
                type: Project.HEADERS_CHANGED,
                changes: change,
              };

              msg = Project.loadProject(projectPK, null, spreadID, true);
              break;

            case 'IMAGE_REMOVED':
              msg = {
                type: IMAGE_REMOVED,
                image: change.changes,
              };

              break;
            case 'SPREAD_DELETED': {
              const currentPages = (yield select(
                Selectors.pagesSelector,
              )).toJS();
              const currentSection = (yield select(
                Selectors.sectionSelector,
              )).toJS();
              const currentSpreads = currentSection.spreads;
              let deletingCurrentPage = false;
              let goToSpread = spreadID;

              let currentSpreadIndex = 0;
              for (let i = 0; i < currentSpreads.length; ++i) {
                if (deletingCurrentPage) break;
                const spread = currentSpreads[i];
                if (spread.id === change.changes.spreadID) {
                  for (let sp = 0; sp < spread.pages.length; ++sp) {
                    const page = spread.pages[sp];
                    if (currentPages[page.pageID] !== undefined) {
                      deletingCurrentPage = true;
                      goToSpread =
                        currentSpreadIndex === currentSpreads.length - 1
                          ? currentSpreads[i - 1]?.id
                          : currentSpreads[i + 1]?.id;
                      break;
                    }
                  }
                }
                currentSpreadIndex = i;
              }

              yield put(Project.loadProject(projectPK, null, goToSpread, true));

              msg = Project.spreadDeleted(change.changes.spreadID);
              break;
            }
            case 'SPREADS_ADDED':
              msg = Project.spreadsAdded(
                change.changes.sectionID,
                change.changes.insertIndex,
                change.changes.spreads,
              );
              yield put(Project.loadProject(projectPK, null, spreadID, true));
              break;
            case 'PAGE_IMAGE_USAGE_CHANGED':
              msg = {
                type: UPDATE_IMAGE_COUNTS,
                spreadID: change.changes.spreadID,
                pages:
                  activePages[0]?.pageType === 'C'
                    ? 'Cover'
                    : activePages[0]?.pageNumber - 1,
                deltas: change.changes.deltas,
              };
              break;
            case 'FILTER_APPLIED_TO_ALL_PAGES':
            case 'SET_BACKGROUND_COLOR_ALL_PAGES':
              //msg = Selection.keepSelection(Pages.reloadSpread());
              msg = Project.loadProject(projectPK, null, spreadID, true);
              break;
            case 'THEME_PAGES_APPLIED':
              msg = Project.loadProject(projectPK, null, spreadID, true);
              break;
            case 'SECTION_SPREADS_CHANGED':
              let spreadToLoad = change.changes.spreadToLoad
                ? change.changes.spreadToLoad
                : change.changes.spreads && spreadIndex > 0
                  ? change.changes.spreads[spreadIndex - 1].id
                  : null;
              // [BWO-137]
              // If spreadToLoad is populated use spreadToLoad
              // Else use spreadIndex to get new spread id after CHANGESET_MUTATE_PAGES_IN_SECTION which causes all spread ids to change
              // spreadIndex-1 because cover page isn't included in changes.spreads
              if (spreadToLoad) {
                msg = Project.loadProject(projectPK, null, spreadToLoad, true);
              } else {
                msg = Project.loadProjectUndoable(projectPK, 'first');
              }
              break;
            case 'PAGE_REFLOWED': {
              yield put(hideModal());
              const currentPages = yield select(Selectors.pagesSelector);
              if (currentPages.get(change.changes.id)) {
                msg = { type: PAGE_UPDATED, page: change.changes };
              }
              break;
            }
            case 'CLEAR_UNDO_STACK':
              //this was implicit in SECTION_REFLOWED, but I changed it to be
              //explicit so we can clear if layout is applied to multiple pages, but
              //not a single page
              yield put(ActionCreators.clearHistory());
              break;
            case 'ACCOUNT_PREFERENCE_UPDATED': {
              const { preferenceName } = change.changes;

              // Only trigger spell check if the ignoredWords preference was updated
              if (preferenceName === 'ignoredWords') {
                try {
                  const data = yield call(spellCheckProject, projectPK);
                  const warnings = yield select(
                    Selectors.projectWarningsSelector,
                  );
                  const newWarnings = warnings.set('spelling', fromJS(data));

                  msg = updateWarnings(newWarnings);
                } catch (e) {
                  console.error(
                    'Error running spell check after ignoredWords preference update:',
                    e,
                  );
                }
              }
              break;
            }
          }

          if (msg) {
            yield put(msg);
          }
        }
      } else if (data && data.resultCode === -1113) {
        yield put(
          showModal(
            'There was a problem saving your changes',
            'This page has been updated by another user and needs to reload. You will need to make your changes again.',
          ),
        );
        yield put(Pages.reloadSpread());
        console.error('got concurrency error for changeset updates: ', error);
      } else {
        yield put(showGenericErrorModal());
        yield put(Pages.reloadSpread());
        console.error('got error for changeset updates: ', error);
      }
    } catch (exception) {
      if (exception?.response?.status === 401) {
        yield put(showLoginModal({ addToCart: false, reload: true }));
      } else {
        yield put(showGenericErrorModal());
        yield put(Pages.reloadSpread());
        console.error(
          'Got exception while trying to send changesets',
          exception,
        );
      }
    }
  }
}

function* updatePagesSaga(action) {
  try {
    if (action.APPLYING_CHANGES) {
      return;
    }

    let pages = action.pages;

    if (List.isList(pages)) {
      pages = pages.toJS(); // only want the top level converted
    }

    // need to grab all page items and put them back in...
    let allPageItems = yield select(Selectors.pageItemsSelector);

    let updatedPages = [];
    for (let p of pages) {
      if (Map.isMap(p)) {
        p = p.toJS();
      }

      let pageItemIDs = [...p.pageItems];

      if (p.background.graphicItem) {
        p.background.graphicItem = allPageItems
          .get(p.background.graphicItem)
          .toJS();
      }

      p.pageItems = pageItemIDs
        .filter((id) => {
          const pi = allPageItems.get(id);
          return pi !== undefined;
        })
        .map((id) => {
          const pi = allPageItems.get(id);
          return pi.toJS();
        });

      updatedPages.push(p);
    }
    yield put(pagesUpdated(updatedPages));
    yield put(changesetAdded());
  } catch (ex) {
    alert('Error saving pages');
    console.error('Error saving pages', ex);
  }
}

function* addPageItemsSaga(action) {
  if (action.propagate != false) {
    yield put(addPageItems(action.pageItems));
    yield put(changesetAdded());
  }
}

function* removePageItemsSaga(action) {
  yield put(removePageItems(action.items));
  yield put(changesetAdded());
}

function* addImageSaga(action) {
  const section = yield select(Selectors.sectionIdSelector);
  yield put(addImage({ ...action.image, sections: [section] }));
  yield put(changesetAdded());
}

function* removeImageSaga(action) {
  yield put(removeImages(action.image));
  yield put(changesetAdded());
}

function* removeUnusedImagesSaga(action) {
  let images = yield select(Selectors.projectImagesSelector);
  let ids = [];
  images.map((value, key) => {
    if (value.get('usage').size === 0) {
      ids.push(key);
    }
  });

  //Images will be removed from the redux store when we get the response back from the server,
  //however we can just do it now so user does not experience a delay
  for (let id of ids) {
    yield put({ type: IMAGE_REMOVED, image: id });
  }

  yield put(removeImages(ids));
  yield put(changesetAdded());
}

function* deleteSpreadSaga(action) {
  yield put(deleteSpread(action.spreadID));
  yield put(changesetAdded());
}

function* duplicateSpreadSaga(action) {
  yield put(duplicateSpread(action.spreadID));
  yield put(changesetAdded());
}

function* addSpreadSaga(action) {
  yield put(addSpread(action.sectionID, action.after, action.before));
  yield put(changesetAdded());
}

function* applyFilterToAllSaga(action) {
  yield put(applyFilterToAll(action.pageID, action.pageItemID, action.filter));
  yield put(changesetAdded());
}

function* applyDropShadowToAllSaga(action) {
  yield put(applyDropShadowToAll(action.pageID, action.pageItemID));
  yield put(changesetAdded());
}

function* updatePagesFromCache(action) {
  // if pages were passed in, update those, otherwise, update all of the pages in the store.
  const allPages = yield select(Selectors.pagesSelector);

  let updatedPages =
    action.pages != undefined
      ? action.pages.map((p) => allPages.get(p.get('id')).toJS())
      : allPages.map((p) => p.toJS()).toArray();

  yield put(updatePages(updatedPages));
}

function denormalizePage(page, pageItems) {
  if (page.background.graphicItem) {
    page.background.graphicItem = pageItems
      .get(page.background.graphicItem)
      .toJS();
  }

  const pageItemIDs = page.pageItems;
  page.pageItems = pageItemIDs.map((id) => {
    return pageItems.get(id).toJS();
  });

  return page;
}

function* updateSpreadFromCache() {
  // i pages were passed in, update those, otherwise, update all of the pages in the store.
  const allPages = yield select(Selectors.pagesSelector);
  const allPageItems = yield select(Selectors.pageItemsSelector);
  const updatedPages = Object.values(allPages.toJS()).map((p) =>
    denormalizePage(p, allPageItems),
  );

  const allImagesMap = yield select(Selectors.projectImagesSelector);

  // Check that our undo/redo is not causing us to reference images that have been removed from the project
  for (const page of updatedPages) {
    for (const pageItem of page.pageItems) {
      if (pageItem.imageID) {
        if (!allImagesMap.has(pageItem.imageID)) {
          // Delete it from the pageItem that is about to be saved
          delete pageItem.imageID;

          // Dispatching this action will ensure the impending render operation won't blow up
          // and also that the page thumb is updated.
          yield put(
            updatePageItemsNoUndo([
              allPageItems.get(pageItem.id).delete('imageID'),
            ]),
          );
        }
      }
    }
  }

  let currentPages = yield select(Selectors.pagesSelector);

  let leftPage = currentPages.first();
  let sections = yield select(Selectors.projectSectionsSelector);
  let currentSectionID = undefined;

  let currentSpreadID = findSpreadID(sections, leftPage, currentSectionID);
  if (!currentSpreadID) {
    //Remove inside cover first page (or page from previous section)
    updatedPages.splice(0, 1);
    currentSpreadID = findSpreadID(
      sections,
      currentPages.skip(1).first(),
      currentSectionID,
    );
  }

  if (currentPages.count() > 1) {
    const lastSpreadID = findSpreadID(
      sections,
      currentPages.skip(1).first(),
      currentSectionID,
    );
    if (!lastSpreadID) {
      //Remove inside cover last page (or page from next section)
      updatedPages.splice(1, 1);
    }
  }

  yield put(updateSpread(currentSpreadID, updatedPages));
}

function findSpreadID(sections, withPage, inSectionID) {
  let spreadID = undefined;
  sections.map((section) => {
    if (!inSectionID || section.get('sectionID') === inSectionID) {
      section.get('spreads').map((spread, spreadIndex) => {
        if (
          spread
            .get('pages')
            .toJS()
            .some((page) => {
              return page.pageID === withPage.get('id');
            })
        ) {
          spreadID = spread.get('id');
        }
      });
    }
  });

  return spreadID;
}

function* saveCurrentSpreadListFromCache() {
  let sections = [];
  let currentSections = yield select(Selectors.projectSectionsSelector);
  sections = currentSections.toJS();

  let msg = [];
  Object.keys(sections).forEach((key) => {
    const section = sections[key];
    msg.push({
      id: key,
      spreads: section.spreads.map((spread) => {
        return spread.id;
      }),
    });
  });
  yield put(setSpreadList(msg));
}

function* reorderSpreadsSaga(action) {
  yield put(setSpreadList([{ id: action.sectionID, spreads: action.spreads }]));
}

function* handlePageItemsUpdated(action) {
  // All we need to do is add it to the changeset by dispatching the action to do that..

  if (action.APPLYING_CHANGES || action.sendChangeset === false) {
    return;
  }

  let updatedPageItems = action.pageItems;

  if (!Array.isArray(updatedPageItems))
    updatedPageItems = Array.of(updatedPageItems);

  // but we have to convert to JS first
  updatedPageItems = updatedPageItems.map((item) => item.toJS());
  //so, let's cleanse out things we probably shouldn't be saving to the database:
  updatedPageItems = updatedPageItems.map((item) => {
    //we will probably never use this, so we're taking it out to reduce size
    if (item.hasOwnProperty('svg')) {
      delete item.svg;
    }
    if (item.hasOwnProperty('spellCheck')) {
      delete item.spellCheck;
    }
    return item;
  });

  yield put(updatePageItems(updatedPageItems));
  yield put(changesetAdded());
}

function* applyLayoutSaga(action) {
  if (isMobile()) {
    yield put(setSidePanelOpened(false));
  }

  if (action.changes.layout.applyTo === 'W') {
    let selectedSize = yield select(Selectors.selectedSize);
    let projectMetadata = yield select(Selectors.projectMetadataSelector);
    let editorDim = yield select(Selectors.editorDimSelector);
    let wrap = yield select(Selectors.wrapSelector);

    let spineWidth = SpineHelper.calculateSpineWidth(
      editorDim.width,
      projectMetadata.get('numPages'),
      null,
      selectedSize.get('sku') || projectMetadata.get('style'),
    );

    action.changes.spinePercentage = spineWidth / editorDim.width;
    action.changes.horizontalWrap = wrap.get('horizontalWrap');
    action.changes.verticalWrap = wrap.get('verticalWrap');
  } else if (action.changes.layout.applyTo === 'Y') {
    yield put(showProcessing());
  }

  yield put(applyLayoutToPages(action.changes));
  yield put(changesetAdded());
}

function* applyPageLayoutSaga(action) {
  yield put(applyPageLayoutToPages(action.changes));
  yield put(changesetAdded());
}

function* applyPageDesignSaga(action) {
  yield put(applyPageDesignToPages(action.changes));
  yield put(changesetAdded());
}

function* applyThemePagesSaga(action) {
  yield put(applyThemePages(action.changes));
  yield put(changesetAdded());
}

function* convertSpreadPagesSaga(action) {
  yield put(Selection.clearSelection());

  let currentPages = yield select(Selectors.pagesSelector);
  let leftPage = currentPages.first();
  let sections = yield select(Selectors.projectSectionsSelector);
  let currentSpreadID = undefined;
  sections.map((section) => {
    section.get('spreads').map((spread, spreadIndex) => {
      if (
        spread
          .get('pages')
          .toJS()
          .some((page) => {
            return page.pageID === leftPage.get('id');
          })
      ) {
        currentSpreadID = spread.get('id');
      }
    });
  });

  if (action.callback) {
    yield put(setApplyLayoutCallback(action.callback));
  }

  yield put(
    convertSpreadPages({
      spreadID: currentSpreadID,
      backgroundID:
        action.background != null ? action.background.get('imageID') : null,
    }),
  );
  yield put(changesetAdded());
}

function* stopThumbsHandler() {
  yield put(stopThumbs());
}

function* addChangesetFromAction(type, action, sendAsArray = false) {
  if (action.APPLYING_CHANGES) {
    return;
  }

  let msg = sendAsArray ? [{ ...action }] : { ...action };
  delete msg.type;
  yield put(addChangeset(type, msg));
  yield put(changesetAdded());
}

function* addChangesetFromActionAsArray(type, action) {
  yield call(addChangesetFromAction, type, action, true);
}

export function* handleCreateThumbnail(action) {
  if (action && action.sendChangeset != undefined && !action.sendChangeset) {
    return;
  }

  try {
    // The page in question is not always the selected page. For example, dragging an image onto the page
    // does not select it. First try to get the page from the page items.
    let pages = null;
    if (action?.pageItems?.length) {
      const pageID = action.pageItems.at(0).get('pageID');
      pages = yield select(Selectors.pagesSelector);
      pages = pages.filter((page) => page.get('id') === pageID).toList();
    }

    if (!pages) {
      pages = yield select(Selectors.selectedPages);
    }

    const section = yield select(Selectors.sectionSelector);
    const sectionType = section.get('type');

    if (!pages || pages.size === 0) {
      pages = yield select(Selectors.pagesSelector);
      pages = pages.toList();
    }

    //filter out inner covers, which are getting through for some reason
    pages = pages.filter((page) => {
      return !(
        sectionType !== PicabooConstants.SECTION_TYPES.TYPE_SOFTCOVER &&
        sectionType !== PicabooConstants.SECTION_TYPES.TYPE_COVER &&
        page.get('pageType') === PicabooConstants.PAGE_TYPES.COVER
      );
    });

    let thumbnailKey = pages.map((page) => page.get('id')).join('-');
    if (thumbnailTasks[thumbnailKey]) {
      yield cancel(thumbnailTasks[thumbnailKey]);
    }

    const allPageItems = yield select(Selectors.pageItemsSelector);
    const images = yield select(Selectors.projectImagesSelector);
    const projectPK = yield select(Selectors.projectPKSelector);

    const projectMeta = yield select(Selectors.projectMetadataSelector);
    const projectType = projectMeta.get('type');
    let isProjectThumb = false;
    const hasHardCover = projectMeta.getIn([
      'preferences',
      'coverType',
      'hard',
    ]);
    const coverColor = projectMeta.get('cover');

    if (
      projectType === PicabooConstants.PRODUCT_TYPES.FRAME ||
      projectType === PicabooConstants.PRODUCT_TYPES.YEARBOOK_AD ||
      projectType === PicabooConstants.PRODUCT_TYPES.COLLAGE ||
      projectType === PicabooConstants.PRODUCT_TYPES.PHOTO_PANEL ||
      projectType === PicabooConstants.PRODUCT_TYPES.CANVAS ||
      projectType === PicabooConstants.PRODUCT_TYPES.METAL_PRINT ||
      projectType === PicabooConstants.PRODUCT_TYPES.MOUSE_PAD ||
      projectType === PicabooConstants.PRODUCT_TYPES.COASTER ||
      projectType === PicabooConstants.PRODUCT_TYPES.NOTEBOOK ||
      projectType === PicabooConstants.PRODUCT_TYPES.JOURNAL ||
      sectionType === PicabooConstants.SECTION_TYPES.TYPE_COVER ||
      sectionType === PicabooConstants.SECTION_TYPES.TYPE_SOFTCOVER ||
      (((projectType === PicabooConstants.PRODUCT_TYPES.CARD &&
        pages.get(0).has('pageNumber')) ||
        projectType === PicabooConstants.PRODUCT_TYPES.CALENDAR ||
        projectType === PicabooConstants.PRODUCT_TYPES.SQUARE_PRINT) &&
        pages.hasIn([0, 'pageNumber', 0]) &&
        pages.get(0).get('pageNumber').get(0) === 1) ||
      (projectType === PicabooConstants.PRODUCT_TYPES.PILLOW &&
        isFirstPageOfPillow(pages, section)) ||
      (projectType === PicabooConstants.PRODUCT_TYPES.TOTE &&
        isFirstPageOfPillow(pages, section)) ||
      (projectType === PicabooConstants.PRODUCT_TYPES.ORNAMENT &&
        isFirstPageOfPillow(pages, section)) ||
      projectType === PicabooConstants.PRODUCT_TYPES.BLANKET ||
      projectType === PicabooConstants.PRODUCT_TYPES.PLAYING_CARDS ||
      projectType === PicabooConstants.PRODUCT_TYPES.CRYSTAL ||
      projectType === PicabooConstants.PRODUCT_TYPES.MUG ||
      projectType === PicabooConstants.PRODUCT_TYPES.PUZZLE ||
      projectType === PicabooConstants.PRODUCT_TYPES.PHOTO_POSTER ||
      (projectType === PicabooConstants.PRODUCT_TYPES.PRINTS &&
        pages.get(0).has('pageNumber') &&
        pages.get(0).get('pageNumber').get(0) === 1)
    ) {
      isProjectThumb = true;
    }

    let wrap = yield select(Selectors.wrapSelector);
    let thumbnailToCreate = {
      projectPK,
      pages,
      pageItems: allPageItems,
      images,
      isProjectThumb,
      isSoftCover:
        sectionType === PicabooConstants.SECTION_TYPES.TYPE_SOFTCOVER &&
        hasHardCover,
      projectType,
      projectMeta,
      wrap,
      sectionID: section.get('sectionID'),
      cover: coverColor,
    };

    thumbnailTasks[thumbnailKey] = yield fork(
      createThumbnailTask,
      thumbnailToCreate,
    );
  } catch (e) {
    console.error('Error generating thumbnail: ' + e.message);
  }
}

function* createThumbnailTask(thumbnailToCreate) {
  try {
    yield put(thumbDelay(true));
    yield delay(1000);
    yield put(thumbDelay(false));

    yield createThumbnail(thumbnailToCreate);
  } catch (e) {
    console.error('create thumbnail task failed', e);
  }
}

function* handleCoverChanged(action) {
  const allPages = yield select(Selectors.pagesSelector);

  if (allPages.size > 0) {
    // select the first page
    yield put(
      Selection.selectPages([allPages.toList().toArray()[0].get('id')]),
    );

    yield call(handleCreateThumbnail, action);
    yield put(Selection.selectPages([])); // the return will have a different page ID so clear out selection
  }
}

function* updateSectionFromCache(action) {
  const sections = yield select(Selectors.projectSectionsSelector);
  const section = sections.get(action.sectionID);

  const changedSpreads = section.get('spreads').toJS();

  changedSpreads.forEach((spread, index) => {
    if (
      spread.pages[0].pageType === PicabooConstants.PAGE_TYPES.COVER &&
      index == 0
    ) {
      return;
    } else if (
      spread.pages.length === 1 &&
      spread.pages[0].pageType === PicabooConstants.PAGE_TYPES.COVER
    ) {
      spread.pageCount = 0;
    } else {
      spread.pageCount = Math.max(
        spread.pages.reduce((accumulator, currentValue) => {
          return currentValue.pageType == PicabooConstants.PAGE_TYPES.COVER
            ? 0
            : (currentValue.ratioBase ? currentValue.ratioBase : 1) +
                accumulator;
        }, 0),
        1,
      );
    }
  });

  yield addChangesetFromAction(CHANGESET_MUTATE_PAGES_IN_SECTION, {
    sectionID: section.get('sectionID'),
    addPagesLayoutType: action.addPagesLayoutType,
    changedSpreads,
  });
}

export function* updateAccountPreferenceSaga(action) {
  const user = yield select(Selectors.userSelector);
  if (!user.get('isGuest')) {
    const prefs = yield select(Selectors.userPrefSelector);
    const value = prefs.get(action.preferenceName);
    if (value !== undefined) {
      yield put(updateAccountPreference(action.preferenceName, value));
      yield put(changesetAdded());
    }
  }
}

function* buildAutoFillChangeSet(action) {
  action.insertAfterSpreadID = '';
  yield addChangesetFromAction(action.type, action);
  yield put(setInsertAfterSpreadID(''));
}

function* setupSagas() {
  // yield takeEvery(ADD_CHANGESET, addChangesetSaga);
  yield fork(pushChangesSaga);
  yield takeEvery(CHANGESET_UPDATE_PAGES, updatePagesSaga);
  yield takeEvery(
    [PageItems.UPDATE_PAGE_ITEMS, PageItems.UPDATE_PAGE_ITEMS_NO_UNDO],
    handlePageItemsUpdated,
  );
  yield takeEvery(
    [
      PageItems.UPDATE_PAGE_ITEMS,
      PageItems.ADD_PAGE_ITEMS,
      SET_BACKGROUND_IMAGE,
      SET_BACKGROUND_COLOR_UNDOABLE,
      SET_BACKGROUND_COLOR_ALL_PAGES,
      SET_BACKGROUND_COLOR,
      Project.CREATE_NEW_PAGE_THUMB,
    ],
    handleCreateThumbnail,
  );
  yield takeLatest(Project.UPLOAD_THUMB, handleCoverChanged);

  // ************************ DON'T ADD ANY MORE TO THIS SECTION FOR GENERIC CHANGE REQUESTS...
  // Look at the generic "addChangesetFromAction" below to see if it can be used first

  // setting a background image both adds an image and updates the page.. so register both
  // yield takeEvery(SET_BACKGROUND_IMAGE, addImageSaga);
  yield takeEvery(
    [
      SET_BACKGROUND_IMAGE,
      SET_BACKGROUND_PATTERN,
      SET_BACKGROUND_COLOR,
      SET_BACKGROUND_COLOR_UNDOABLE,
    ],
    updatePagesFromCache,
  );

  // ************************ DON'T ADD ANY MORE TO THIS SECTION FOR GENERIC CHANGE REQUESTS...
  // Look at the generic "addChangesetFromAction" below to see if it can be used first
  yield takeEvery(ADD_PAGE_ITEMS, addPageItemsSaga);
  // yield takeEvery(REMOVE_PAGE_ITEMS, removePageItemsSaga);

  // ************************ DON'T ADD ANY MORE TO THIS SECTION FOR GENERIC CHANGE REQUESTS...
  // Look at the generic "addChangesetFromAction" below to see if it can be used first
  yield takeEvery(Project.ADD_PROJECT_IMAGE, addImageSaga);
  yield takeEvery(Project.REMOVE_PROJECT_IMAGE, removeImageSaga);
  yield takeEvery(Project.REMOVE_PROJECT_UNUSED_IMAGES, removeUnusedImagesSaga);

  // ************************ DON'T ADD ANY MORE TO THIS SECTION FOR GENERIC CHANGE REQUESTS...
  // Look at the generic "addChangesetFromAction" below to see if it can be used first
  yield takeEvery(Layouts.APPLY_LAYOUT_TO_PAGES, applyLayoutSaga);
  yield takeEvery(Layouts.APPLY_PAGELAYOUT_TO_PAGES, applyPageLayoutSaga);
  yield takeEvery(Layouts.APPLY_PAGEDESIGN_TO_PAGE, applyPageDesignSaga);
  yield takeEvery(Pages.CONVERT_SPREAD_PAGES, convertSpreadPagesSaga);
  yield takeEvery(Pages.DELETE_SPREAD, deleteSpreadSaga);
  yield takeEvery(Pages.DUPLICATE_SPREAD, duplicateSpreadSaga);
  yield takeEvery(Pages.ADD_SPREAD, addSpreadSaga);
  yield takeEvery(Pages.REORDER_SECTION_SPREADS, reorderSpreadsSaga);
  yield takeEvery(Pages.APPLY_FILTER_TO_ALL_PAGES, applyFilterToAllSaga);
  yield takeEvery(
    Pages.APPLY_DROP_SHADOW_TO_ALL_PAGES,
    applyDropShadowToAllSaga,
  );

  // generic ones that just add the changeset and dispatch changesAdded
  yield takeEvery(Project.SAVE_PAGES_FROM_STORE, updatePagesFromCache);
  yield takeEvery(Project.SAVE_SPREAD_FROM_STORE, updateSpreadFromCache);
  yield takeEvery(
    Project.SAVE_CURRENT_SPREADS_FROM_STORE,
    saveCurrentSpreadListFromCache,
  );
  yield takeEvery(
    REMOVE_PAGE_ITEMS,
    addChangesetFromAction,
    CHANGESET_REMOVE_PAGE_ITEMS,
  );
  yield takeEvery(
    Project.ADD_PROJECT_IMAGE_REF,
    addChangesetFromActionAsArray,
    CHANGESET_ADD_IMAGE_REF,
  );
  yield takeEvery(
    Images.ADD_IMAGE_TAG,
    addChangesetFromAction,
    CHANGESET_ADD_IMAGE_TAG,
  );
  yield takeEvery(
    Project.DELETE_PROJECT_IMAGE_REF,
    addChangesetFromActionAsArray,
    CHANGESET_DELETE_IMAGE_REF,
  );
  yield takeEvery(
    Project.TOGGLE_ORIENTATION,
    addChangesetFromAction,
    CHANGESET_TOGGLE_ORIENTATION,
  );
  yield takeEvery(
    Project.RENAME_PROJECT,
    addChangesetFromAction,
    CHANGESET_RENAME_PROJECT,
  );
  yield takeEvery(
    Project.HEADERS_CHANGED,
    addChangesetFromAction,
    CHANGESET_UPDATE_HEADERS,
  );
  yield takeEvery(
    Project.UPDATE_HEADERS,
    addChangesetFromAction,
    CHANGESET_UPDATE_HEADERS,
  );
  yield takeEvery(
    Project.CHANGE_PROJECT_STYLE,
    addChangesetFromAction,
    CHANGESET_CHANGE_PROJECT_STYLE,
  );
  yield takeEvery(
    CHANGESET_UPDATE_SPREAD,
    addChangesetFromAction,
    CHANGESET_UPDATE_SPREAD,
  );
  yield takeEvery(
    CHANGESET_SET_SPREAD_LIST,
    addChangesetFromAction,
    CHANGESET_SET_SPREAD_LIST,
  );
  yield takeEvery(CHANGESET_AUTOFILL_PAGES, stopThumbsHandler);
  yield takeEvery(CHANGESET_AUTOFILL_PAGES, buildAutoFillChangeSet);
  yield takeEvery(
    CHANGESET_ROTATE_PRINT,
    addChangesetFromAction,
    CHANGESET_ROTATE_PRINT,
  );
  yield takeEvery(
    Pages.UPDATE_PAGE_THUMB,
    addChangesetFromAction,
    CHANGESET_UPDATE_PAGE_THUMB,
  );
  yield takeEvery(
    User.UPDATE_USER_PREFERENCE,
    addChangesetFromAction,
    CHANGESET_UPDATE_ACCOUNT_PREFERENCE,
  );
  yield takeEvery(
    SET_BACKGROUND_COLOR_ALL_PAGES,
    addChangesetFromAction,
    CHANGESET_SET_BACKGROUND_COLOR_ALL_PAGES,
  );
  yield takeEvery(
    SET_BACKGROUND_ALL_PAGES,
    addChangesetFromAction,
    CHANGESET_SET_BACKGROUND_ALL_PAGES,
  );

  //yield takeEvery(Pages.DELETE_PAGE, addChangesetFromAction, CHANGESET_DELETE_PAGE);
  yield takeEvery(Sections.DELETE_PAGE, updateSectionFromCache);
  yield takeEvery(
    [Sections.ADD_PAGES, Sections.ADD_PRINTS],
    updateSectionFromCache,
  );
  yield takeEvery(Sections.ADD_PAGE_SQUAREPRINT, updateSectionFromCache);
  yield takeEvery(
    Sections.ADD_PRINT_PAGE,
    addChangesetFromAction,
    CHANGESET_ADD_PRINT_PAGE,
  );
  yield takeEvery(Sections.MOVE_PAGE, updateSectionFromCache);
}

/** ***********************************************************************
 *  Main Export
 *  *********************************************************************** */
export default {
  reducer: changesetReducer,
  saga: setupSagas,
};
